/**
 * 工程表の画面 /groups/:id/schedule（#100、docs/39 §3.1、Figma A・A2。#101 で §4.2：見積から作る・雛形・中止・遅れ・出来高。
 * #105 で §4.8：作業の領域を広く・画面の下まで（Figma A3）・中止を工程ごとに（A3b）・雨でも作業する）。
 *   usePageName("工程表")、戻るは「‹ 行き先」（行き先が題と同じ{project}なら「‹ {project}」）
 *   上から：細い藍の帯（64px。戻る・題＝{project}名・しるし／右に数字4つ＝進み具合（予定）・次の節目・最後の節目（遅れ）・出来高）
 *   → 操作の列（1段。日／旬／月・‹ 今日 ›・期間・保存の知らせ ……元に戻す／やり直す・中止・休み・まとめてずらす・⋮＝休みの日・金額・表の幅ほか）
 *   → 遅れの知らせ（1行）→ 工程表（画面の残りの高さいっぱい。中で縦・横にスクロール。日付の見出し・左の表・下の「〜を追加」は動かない）。凡例は表の見出しの「？」
 *   ・空の工程表は4つのカード（見積から・雛形から・前の案件からうつす・白紙から）
 *   ・自動保存（打つ欄は 0.5秒後、ドラッグ・選ぶものはすぐ）。PUT は工程表まるごと（中止・会社の休みを使うかも）＋ baseVersion。409 なら最新を読み直して知らせる。
 *     送る順番・離れるときの確かめ・出力の前の保存待ちは共通の仕組み（lib/saveQueue.tsx の useSaveQueue、#179 の3）
 *   ・見積・雛形・前の案件から足すのは API（金額は DB が決める）。足す前に送っていない変更を送り、足したあとは「元に戻す」で消せる
 *   ・元に戻す：画面の中に 100 回ぶん（工程表まるごとの控え）。再読み込みで消える
 *   ・計算（終わり・押し出し・進み・遅れ・出来高）は packages/contract の関数（API と同じ）。画面は返ってきた形に合わせる
 * #102（docs/39 §5.2・§5.3）：
 *   ・幅 1100px 以上はいつでも直せる。768〜1099px（タブレット）は同じ形で「見るだけ／直す」のスイッチ（既定は見るだけ・開くたびに見るだけ）。
 *     768px 未満（スマホ）は Phone.tsx（E2・E3・E4）
 *   ・人数の列（#297 から ⋮「表示設定」の「人」で既定は出さない・ON にした人のブラウザに覚える。2行目の「・n人」は #297 でやめた。
 *     列を出していないときは{task}の ⋮「人数」）
 *   ・重なり（API の conflicts）：2行目の朱の「重なり」→ 吹き出し（相手・工程表を開く・「10/17(土) から始める」）
 *   ・?row=<id> で開いたら その行を選び、縦横にスクロールして見せる（重なりがあれば吹き出しを開く）
 *   ・2本指で拡大・縮小（日の表示の1日の幅 12〜48px）
 * #103（docs/39 §6.3.4）：遅れの知らせの下に「協力会社からの報告 n件」の1行（「見る」でこの案件の分の吹き出し。反映の前に保存待ちの変更を送る）、
 *   工程の2行目に「報告」「写真 n」（写真を大きく）、工程の ⋮「{業者}にリンクを送る」
 */
import {
  SCHEDULE_JUN_TITLE,
  WEEKDAY_JA,
  addDays,
  blockOf,
  calendarForRow,
  changedTaskCount,
  conflictStartDate,
  copyRow,
  dateRuns,
  datesText,
  dayNo,
  defaultStartAfter,
  defaultStartAt,
  delayedRows,
  earnedOf,
  extendByDelay,
  headingIndexOf,
  keepDelay,
  lastMilestoneOf,
  mdHmJst,
  mdOf,
  mdwJaOf,
  mdwLongOf,
  mdwOf,
  moveRow,
  newRow,
  nextMilestoneOf,
  normalizeSchedule,
  plannedProgressOf,
  projectOf,
  removeRowFromStops,
  removeRows,
  rowStopDates,
  scheduleAddressee,
  scheduleApiBase,
  scheduleBriefOf,
  settleDelayAck,
  shiftFrom,
  shiftWithSuccessors,
  shownDelayOf,
  snapToWorkDay,
  spanText,
  startAfter,
  tidyRowDays,
  tidyDayNotes,
  setDayNote,
  tidyStops,
  toggleRowDay,
  toggleSiteDay,
  pruneRowDays,
  releaseStartRowDays,
  workCalendar,
  workDaysBetween,
  wouldCreateCycle,
  ymdOfDayNo,
  type CompanyOffDay,
  type GroupDetail,
  type Holiday,
  type Schedule,
  type ScheduleAssignees,
  type ScheduleConflict,
  type ScheduleDoc,
  type ScheduleReport,
  type ScheduleReportApplied,
  type ScheduleRowPhoto,
  type ScheduleRow,
  type ScheduleRowDay,
  type ScheduleDayNote,
  type ScheduleRowKind,
  type ScheduleStop,
  type ScheduleTemplateItem,
  type ScheduleView,
  type ScheduleSource,
  type StopTarget,
  type WorkCalendar,
  TASK_STATE_LABEL,
  doneCountOf,
  keepStartDelay,
  nextTaskState,
  setProgressPct,
  setTaskState,
  startDelayedRows,
  taskStateOf,
  type TaskState,
} from "@genbaryoku/contract";
import { PARTNER_LINK_MSG, SCHEDULE_EXPORT_MSG, SCHEDULE_MSG, showsRain, stopIcon } from "./msg.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { backTitle, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { CreateBand, CrIcon } from "../../components/Create.jsx";
import { menuGroups, RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { yen } from "../../lib/num.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { DisplayDialog, FromEstimateDialog, FromGroupDialog, FromTemplateDialog, OffDaysDialog, RowDaysDialog, RowFieldsDialog, SaveTemplateDialog, ShiftFromDialog, StopsDialog, useEsc, type FromEstimateSubmit } from "./Dialogs.jsx";
import { DayNotePop, DayNotesDialog, type DayNoteTarget } from "./DayNotes.jsx";
import { Gantt, PctContext, type GanttOps, type Save } from "./Gantt.jsx";
import { ConflictPop } from "./Overlap.jsx";
import { DelayBand, StartCards, StartDelayBand } from "./Panels.jsx";
import { PartnerLinkDialog } from "./PartnerLink.jsx";
import { PhotoViewer, ReportsNotice, useReportRefresh } from "./Reports.jsx";
import { useCrewColumn, usePinchZoom, useWide } from "./hooks.js";
import { EditSwitch, PhoneSchedule } from "./Phone.jsx";
import { ExportDialog, type GroupExportTarget } from "./Export.jsx";
import { AttachDialog } from "./Standalone.jsx";
import { assigneeLabel } from "./AssigneeInput.jsx";
import { subscribeTextSize } from "../../lib/view.js";
import { ALL_COLS, AMOUNT_W, CREW_W, FIXED_W, NAME_MANUAL_MIN, NAME_MAX, PX, STEP_DAYS, monthStartOf, nameColumnWidth, nameMeasuresOf, rangeOf, scaleOf, todayLocal, type TableCols, type View } from "./timeline.js";
import { PcLink } from "../../components/PcLink.jsx";

/** 編集中の工程表。rowDays＝{task}の日ごとの休み・作業（#293）・workDays＝{siteShort}の作業日（#296）・dayNotes＝日のメモ（#299）。元に戻すの控えにも入る */
type EditDoc = ScheduleDoc & {
  offWeekdays: number[];
  holidayOff: boolean;
  useCompanyOffDays: boolean;
  stops: ScheduleStop[];
  rowDays: ScheduleRowDay[];
  workDays: string[];
  dayNotes: ScheduleDayNote[];
};
const docOf = (s: Schedule): EditDoc => ({
  rows: s.rows,
  links: s.links,
  offWeekdays: s.offWeekdays,
  holidayOff: s.holidayOff,
  useCompanyOffDays: s.useCompanyOffDays,
  stops: s.stops,
  rowDays: s.rowDays ?? [],
  workDays: s.workDays ?? [],
  dayNotes: s.dayNotes ?? [],
});
const HISTORY = 100;
/**
 * 工程・担当の列の幅（#102 §5.1 の2）：ふだんは中身に合わせる（168〜320px）。手で変えた幅だけ その人のブラウザに覚える。
 * 前の「表の幅」（gb.sch.leftWidth＝左の表まるごと）は読まずに消す。
 * #296（docs/102 §2.4.1）・#298：⋮「表示設定」の表の幅「標準／広い／自動」＝無し（標準）・"wide"・"auto"。数は手で変えた幅
 */
const NAME_KEY = "gb.sch.nameWidth";
const OLD_LEFT_KEY = "gb.sch.leftWidth";
type NamePref = number | "wide" | "auto" | null;
const readName = (): NamePref => {
  try {
    window.localStorage.removeItem(OLD_LEFT_KEY);
    const raw = window.localStorage.getItem(NAME_KEY);
    if (raw === "wide" || raw === "auto") return raw;
    const v = raw == null ? NaN : Number(raw);
    return Number.isFinite(v) && v >= NAME_MANUAL_MIN ? v : null;
  } catch {
    return null;
  }
};
const writeName = (v: NamePref) => {
  try {
    if (v == null) window.localStorage.removeItem(NAME_KEY);
    else window.localStorage.setItem(NAME_KEY, typeof v === "number" ? String(Math.round(v)) : v);
  } catch {
    /* 覚えられなくても使える */
  }
};
/** 表の幅の「標準」＝自動幅に、画面の幅が 1440px 以上なら ＋60px。「広い」＝標準 ＋120px（#296） */
const STD_BONUS = 60;
const WIDE_EXTRA = 120;

/** 表の列（#296、docs/102 §2.4.1）：開始・日数・状況を出すか。その人のブラウザに覚える（全部の{schedule}で同じ） */
const COLS_KEY = "gb.sch.cols";
const readCols = (): TableCols => {
  try {
    const v = JSON.parse(window.localStorage.getItem(COLS_KEY) ?? "null") as Partial<TableCols> | null;
    return { start: v?.start !== false, days: v?.days !== false, state: v?.state !== false };
  } catch {
    return ALL_COLS;
  }
};
const writeCols = (c: TableCols) => {
  try {
    window.localStorage.setItem(COLS_KEY, JSON.stringify(c));
  } catch {
    /* 覚えられなくても使える */
  }
};

/** 概略（#291）：その人のブラウザに覚える */
const SUMMARY_KEY = "gb.sch.summary";
const readSummary = (): boolean => {
  try {
    return window.localStorage.getItem(SUMMARY_KEY) === "1";
  } catch {
    return false;
  }
};
const writeSummary = (on: boolean) => {
  try {
    window.localStorage.setItem(SUMMARY_KEY, on ? "1" : "0");
  } catch {
    /* 覚えられなくても使える */
  }
};

/** バーの日付を常に表示（#291 §2.1.1）：日の表示でも全部の行のバーの両端に日付。その人のブラウザに覚える */
const BARDATES_KEY = "gb.sch.barDates";
const readBarDates = (): boolean => {
  try {
    return window.localStorage.getItem(BARDATES_KEY) === "1";
  } catch {
    return false;
  }
};
const writeBarDates = (on: boolean) => {
  try {
    window.localStorage.setItem(BARDATES_KEY, on ? "1" : "0");
  } catch {
    /* 覚えられなくても使える */
  }
};

/**
 * 工程表の見出しの情報（#291）：案件の工程表は案件から、案件なしの工程表は工程表の題・{customer}・担当から。
 * 編集部（ScheduleEditor）はこれだけを見る（案件の画面の GroupDetail を直接見ない）
 */
export type SchedHead = {
  groupId: string | null;
  scheduleId: string;
  name: string;
  clientName: string;
  clientHonorific: string;
  staffName: string;
  /** 案件なしの{customer}（紐づけの初期値） */
  partnerId: string | null;
  /** 案件の見積の枚数（失注は数えない。案件なしは 0） */
  estimates: number;
  /** 案件なしの{schedule}の題 */
  title: string;
};
const headOfGroup = (g: GroupDetail, s: Schedule | null): SchedHead => ({
  groupId: g.id,
  scheduleId: s?.id ?? "",
  name: g.name,
  clientName: g.client.name,
  clientHonorific: g.client.honorific,
  staffName: g.staffName,
  partnerId: g.client.id,
  estimates: g.jobs.filter((j) => j.status !== "lost").length,
  title: s?.title ?? "",
});
const headOfLoose = (s: Schedule): SchedHead => ({
  groupId: null,
  scheduleId: s.id,
  name: s.title,
  clientName: s.partner?.fullName ?? "",
  clientHonorific: s.partner?.honorific ?? "",
  staffName: s.owner?.name ?? "",
  partnerId: s.partner?.id ?? null,
  estimates: 0,
  title: s.title,
});

/** /groups/:id/schedule（案件の工程表）と /schedules/:sid（案件なしの工程表。案件に紐づいていれば案件の道へ転送。#291） */
export function SchedulePage() {
  const { sid } = useParams();
  return sid ? <LooseSchedulePage sid={sid} /> : <GroupSchedulePage />;
}

function LooseSchedulePage({ sid }: { sid: string }) {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const sq = useQuery({
    queryKey: ["schedule", "s", sid],
    queryFn: () => api<ScheduleView>("GET", `/schedules/${sid}`),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  usePageName(L.schedule);
  const back = useBackNav({ to: "/schedule", label: L.schedule });
  const gid = sq.data?.schedule?.groupId ?? null;
  useEffect(() => {
    // 案件に紐づいた工程表は案件の道で開く（URL は /groups/:id/schedule）
    if (gid) nav(`/groups/${gid}/schedule${sp.toString() ? `?${sp.toString()}` : ""}`, { replace: true });
  }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (sq.isPending || !sq.isFetchedAfterMount || gid) return <p className="hint">読み込み中…</p>;
  if (sq.isError || !sq.data.schedule) {
    return (
      <div className="sch-page">
        <div className="paper sch-empty" data-testid="sch-option">
          <ErrorText>{sq.isError ? messageOf(sq.error) : SCHEDULE_MSG.notFound}</ErrorText>
        </div>
        {back.dialog}
      </div>
    );
  }
  const view = sq.data;
  return (
    <ScheduleEditor
      key={view.schedule!.id}
      head={headOfLoose(view.schedule!)}
      initial={view}
      back={back}
      onDeleted={() => {
        qc.removeQueries({ queryKey: ["schedule", "s", sid] });
        void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
        nav("/schedule", { replace: true, state: { notice: SCHEDULE_MSG.scheduleDeleted } });
      }}
    />
  );
}

function GroupSchedulePage() {
  const { id = "" } = useParams();
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const gq = useQuery({ queryKey: ["group", id], queryFn: () => api<GroupDetail>("GET", `/groups/${id}`), retry: false });
  // 開くたびに必ず最新を読んでから描く（9/30 射場さん：案件から開くと何も出なかった。作った直後の空の工程表の控えが
  // 5分残り（TanStack Query は gcTime を使った画面のうち長いほうにする）、古い版のまま描いていた。保存の途中で 409 になり最新が出ていた）
  const sq = useQuery({
    queryKey: ["schedule", id],
    queryFn: () => api<ScheduleView>("GET", `/groups/${id}/schedule`),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  usePageName(L.schedule);
  const back = useBackNav({ to: `/groups/${id}`, label: gq.data?.name ?? L.project });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();

  // 前に開いたときの控えがあっても、この画面で読み直すまでは描かない（古い版で直し始めない）
  if (gq.isPending || sq.isPending || !sq.isFetchedAfterMount) return <p className="hint">読み込み中…</p>;
  if (gq.isError) return <div className="soonbox"><ErrorText>{messageOf(gq.error)}</ErrorText></div>;
  const g = gq.data;
  const band = <CreateBand back={back} title={g.name} deco="house" chips={<BandChips head={headOfGroup(g, null)} rows={sq.data?.schedule?.rows ?? []} />} />;
  if (sq.isError) {
    // 使えない会社（モジュール schedule が OFF＝404 MODULE_OFF。前の 403 OPTION_REQUIRED も）：オプションの案内（#142 段5。営業力も同じ）
    const opt = sq.error instanceof ApiError && (sq.error.code === "MODULE_OFF" || sq.error.code === "OPTION_REQUIRED");
    return (
      <div className="sch-page">
        {band}
        <div className="paper sch-empty" data-testid="sch-option">
          <p style={{ margin: "0 0 12px" }}>{opt ? SCHEDULE_MSG.optionOff : messageOf(sq.error)}</p>
          {opt ? <PcLink className="btn" to="/account/plan">料金プランへ</PcLink> : null}
        </div>
        {back.dialog}
      </div>
    );
  }
  const view = sq.data;
  if (!view.schedule) {
    return (
      <div className="sch-page">
        {band}
        <div className="paper sch-empty">
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(undefined);
              try {
                const v = await api<ScheduleView>("POST", `/groups/${id}/schedule`);
                qc.setQueryData(["schedule", id], v);
                void qc.invalidateQueries({ queryKey: ["group", id] });
              } catch (e) {
                setErr(messageOf(e));
              } finally {
                setBusy(false);
              }
            }}
            data-testid="sch-create"
          >
            {SCHEDULE_MSG.createFromEstimate}
          </button>
          <ErrorText>{err}</ErrorText>
        </div>
        {back.dialog}
      </div>
    );
  }
  return (
    <ScheduleEditor
      key={view.schedule.id}
      head={headOfGroup(g, view.schedule)}
      initial={view}
      back={back}
      onDeleted={() => {
        qc.removeQueries({ queryKey: ["schedule", id] });
        void qc.invalidateQueries({ queryKey: ["group", id] });
        nav(`/groups/${id}?tab=schedule`, { replace: true, state: { notice: SCHEDULE_MSG.scheduleDeleted } });
      }}
    />
  );
}

/** 帯のしるし：{customer}・工期・担当（案件なしの{schedule}は「案件なし」のチップも。#291） */
function BandChips({ head, rows }: { head: SchedHead; rows: readonly ScheduleRow[] }) {
  const b = scheduleBriefOf(rows);
  return (
    <>
      {head.groupId ? null : (
        <span className="crchip" data-testid="sch-band-loose">
          {SCHEDULE_MSG.noProject}
        </span>
      )}
      {head.clientName ? (
        <span className="crchip">
          <CrIcon k="building" />
          {head.clientName}
        </span>
      ) : null}
      {b.start && b.end ? (
        <span className="crchip" data-testid="sch-band-period">
          <CrIcon k="cal" />
          {t("{period}")} {mdwOf(b.start).replace(/\(.\)$/, "")}〜{mdwOf(b.end).replace(/\(.\)$/, "")}
        </span>
      ) : null}
      {head.staffName ? (
        <span className="crchip">
          <CrIcon k="user" />
          担当 {head.staffName}
        </span>
      ) : null}
    </>
  );
}

type Ask =
  | { t: "heading"; index: number; n: number; name: string }
  | { t: "preds"; id: string }
  | { t: "shift"; id: string }
  | { t: "deleteSchedule" }
  | { t: "fromEstimate" }
  | { t: "fromTemplate" }
  | { t: "fromGroup" }
  | { t: "saveTemplate" }
  | { t: "stops"; rowId?: string; date?: string }
  | { t: "rowDays"; id: string }
  | { t: "dayNotes"; id: string }
  | { t: "rowFields"; id: string; mode: "dates" | "state" | "crew" }
  | { t: "cols" }
  | { t: "shiftFrom" }
  | null;

/** 休みの見出し「日・祝・会社の休み」 */
const offTextOf = (d: EditDoc, companyOff: readonly CompanyOffDay[]) =>
  [...d.offWeekdays.map((w) => WEEKDAY_JA[w]), ...(d.holidayOff ? ["祝"] : []), ...(d.useCompanyOffDays && companyOff.length ? ["会社の休み"] : [])].join("・") || "なし";

function ScheduleEditor({ head, initial, back, onDeleted }: { head: SchedHead; initial: ScheduleView; back: ReturnType<typeof useBackNav>; onDeleted: () => void }) {
  const { toast, node: toastNode } = useToast();
  const qc = useQueryClient();
  const today = useMemo(() => todayLocal(), []);
  const wide = useWide(1100);
  const tablet = useWide(768);
  const phone = !tablet;
  // 1100px 未満は「見るだけ／直す」のスイッチ（既定は見るだけ。開くたびに見るだけに戻る。#102）
  const [editOn, setEditOn] = useState(false);
  const readOnly = !wide && !editOn;
  // 工程・担当の列：手で変えた幅（覚えたもの）か、中身に合わせた幅（#102 §5.1 の2）
  const [namePref, setNamePref] = useState<NamePref>(() => readName());
  const [cols, setColsState] = useState<TableCols>(() => readCols());
  const setCols = (c: TableCols) => (setColsState(c), writeCols(c));
  const wide1440 = useWide(1440);
  const [showAmount, setShowAmount] = useState(false);
  // 人の列（#297）：既定は出さない。ON にした人のブラウザに覚える（全体の{schedule}の「その日の人数」と同じ値）
  const [showCrew, setShowCrew] = useCrewColumn();
  const [view, setView] = useState<View>("day");
  const [summary, setSummaryState] = useState<boolean>(() => readSummary());
  const setSummary = (on: boolean) => (setSummaryState(on), writeSummary(on));
  const [barDatesAll, setBarDatesAllState] = useState<boolean>(() => readBarDates());
  const setBarDatesAll = (on: boolean) => (setBarDatesAllState(on), writeBarDates(on));
  // 2本指で拡大・縮小した日の表示の1日の幅（無ければ既定の 24px）
  const [dayPx, setDayPx] = useState<number | null>(null);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>(initial.conflicts ?? []);
  const conflictOf = useMemo(() => new Map(conflicts.map((c) => [c.rowId, c])), [conflicts]);
  const [conflictPop, setConflictPop] = useState<{ rowId: string; anchor: HTMLElement } | null>(null);
  // 協力会社からの報告（#103）：この工程表の確かめ待ち・反映した写真
  const [reports, setReports] = useState<ScheduleReport[]>(initial.reports ?? []);
  const [photos, setPhotos] = useState<ScheduleRowPhoto[]>(initial.photos ?? []);
  const [reportFor, setReportFor] = useState<{ rowId: string; anchor: HTMLElement } | null>(null);
  const [viewer, setViewer] = useState<{ ids: string[]; i: number; caps: string[] } | null>(null);
  const [linkFor, setLinkFor] = useState<{ id: string; name: string } | null>(null);
  const refreshReports = useReportRefresh();
  const viewRef = useRef<View>(view);
  viewRef.current = view;
  const [doc, setDocState] = useState<EditDoc>(() => docOf(initial.schedule!));
  const docRef = useRef(doc);
  // 会社の設定「進捗を％で入力」（#292。false＝未着手／着手／完了の3つ。答えのたびに取り直す）
  const [pct, setPct] = useState<boolean>(initial.progressPct ?? true);
  const pctRef = useRef(pct);
  pctRef.current = pct;
  const [holidays, setHolidays] = useState<Holiday[]>(initial.holidays);
  const holidaysRef = useRef(holidays);
  const [companyOff, setCompanyOff] = useState<CompanyOffDay[]>(initial.companyOffDays);
  const companyOffRef = useRef(companyOff);
  const version = useRef(initial.schedule!.version);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ask, setAsk] = useState<Ask>(null);
  /** 日のメモの吹き出し（#299。右クリックの「この日のメモ」・点線・見出しの長押し） */
  const [notePop, setNotePop] = useState<DayNoteTarget | null>(null);
  // 出力（#104）：見るだけのときも開ける
  const [exportOpen, setExportOpen] = useState(false);
  const [offOpen, setOffOpen] = useState(false);
  const [blank, setBlank] = useState(false);
  const past = useRef<EditDoc[]>([]);
  const future = useRef<EditDoc[]>([]);
  const lastKey = useRef<{ key: string; at: number } | null>(null);
  const [, bump] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  // 案件の工程表は /groups/:id/schedule、案件なしの工程表は /schedules/:id（同じ処理。#291）
  const url = scheduleApiBase({ groupId: head.groupId, scheduleId: head.scheduleId });
  const nav = useNavigate();
  const [attachOpen, setAttachOpen] = useState(false);

  // 工程・担当の列の幅：中身に合わせる（フォントを読み終えたら測り直す）。打っている間は広がるだけで縮めず、開き直したときに合わせ直す
  const [fontsReady, setFontsReady] = useState(0);
  useEffect(() => {
    let live = true;
    void document.fonts?.ready.then(() => live && setFontsReady((n) => n + 1));
    // 文字の大きさを変えたとき（#115）も測り直す
    const off = subscribeTextSize(() => setFontsReady((n) => n + 1));
    return () => {
      live = false;
      off();
    };
  }, []);
  const candsForWidth = useRef<ScheduleAssignees | undefined>(undefined);
  const measured = useMemo(
    () => nameColumnWidth(nameMeasuresOf(doc.rows, (r) => assigneeLabel(r.assignee, candsForWidth.current), { overlap: new Set(conflictOf.keys()) })),
    [doc.rows, fontsReady, conflictOf], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const autoName = useRef<{ fonts: number; w: number }>({ fonts: -1, w: 0 });
  if (autoName.current.fonts !== fontsReady) autoName.current = { fonts: fontsReady, w: measured };
  else autoName.current.w = Math.max(autoName.current.w, measured);
  const nameMax = typeof window === "undefined" ? NAME_MAX : Math.max(NAME_MAX, Math.floor(window.innerWidth * 0.6) - FIXED_W);
  // 表の幅（#296）：標準＝自動幅（＋60px・1440px 以上）・広い＝標準＋120px・自動幅＝中身に合わせたまま・数＝手で変えた幅
  const stdW = autoName.current.w + (wide1440 ? STD_BONUS : 0);
  const prefW = namePref == null ? stdW : namePref === "wide" ? stdW + WIDE_EXTRA : namePref === "auto" ? autoName.current.w : namePref;
  const nameW = Math.max(NAME_MANUAL_MIN, Math.min(nameMax, prefW));
  const extraW = (showAmount ? AMOUNT_W : 0) + (showCrew ? CREW_W : 0);
  const left = nameW + FIXED_W + extraW;

  const calOf = (d: EditDoc): WorkCalendar => workCalendar(d, holidaysRef.current, { companyOffDays: companyOffRef.current, stops: d.stops, rowDays: d.rowDays, workDays: d.workDays });
  const cal = useMemo(
    () => workCalendar(doc, holidays, { companyOffDays: companyOff, stops: doc.stops, rowDays: doc.rowDays, workDays: doc.workDays }),
    [doc.offWeekdays, doc.holidayOff, doc.useCompanyOffDays, doc.stops, doc.rowDays, doc.workDays, holidays, companyOff], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const setDoc = (d: EditDoc) => {
    docRef.current = d;
    setDocState(d);
  };
  const applyView = (v: ScheduleView) => {
    setConflicts(v.conflicts ?? []);
    setReports(v.reports ?? []);
    setPhotos(v.photos ?? []);
    version.current = v.schedule!.version;
    holidaysRef.current = v.holidays;
    setHolidays(v.holidays);
    companyOffRef.current = v.companyOffDays;
    setCompanyOff(v.companyOffDays);
    setPct(v.progressPct ?? true);
    setDoc(docOf(v.schedule!));
  };
  const bodyOf = (d: EditDoc) => ({
    baseVersion: version.current,
    offWeekdays: d.offWeekdays,
    holidayOff: d.holidayOff,
    useCompanyOffDays: d.useCompanyOffDays,
    stops: d.stops,
    rowDays: d.rowDays,
    workDays: d.workDays,
    dayNotes: d.dayNotes,
    rows: d.rows,
    links: d.links,
  });

  /* ---------------- 保存（自動。#179 の3：共通の仕組み lib/saveQueue.tsx の useSaveQueue） ---------------- */
  // 送るのは1本ずつ（送っている最中に直したら、終わってから続けて送る）・送れなかった直しは残して上の帯で知らせる・
  // 離れるとき（‹・画面の中のリンク・ブラウザの戻る・閉じる）は送り終えてから移る（送れなければ「続ける／破棄」）・
  // 出力（Excel・PDF・CSV）・見積から足す・報告の反映・雛形の保存の前は送り終えるのを待つ。打つ欄は 0.5 秒後、ドラッグ・選ぶものはすぐ
  /** 409（ほかの人が修正）で最新を読み込んだ直後：上の帯に「保存しました」を出さない（次に直すまで） */
  const conflicted = useRef(false);
  const sendDoc = async () => {
    const sent = docRef.current;
    try {
      const v = await api<ScheduleView>("PUT", url, bodyOf(sent));
      version.current = v.schedule!.version;
      setConflicts(v.conflicts ?? []);
      setReports(v.reports ?? []);
      setPhotos(v.photos ?? []);
      holidaysRef.current = v.holidays;
      setHolidays(v.holidays);
      companyOffRef.current = v.companyOffDays;
      setCompanyOff(v.companyOffDays);
      setPct(v.progressPct ?? true);
      // 送ったあとに直していなければ、API がそろえた形に合わせる（終わりの日・押し出し・金額は API が正）
      if (docRef.current === sent) setDoc(docOf(v.schedule!));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === "SCHEDULE_CONFLICT") {
        const latest = e.problem.latest as ScheduleView | undefined;
        if (latest?.schedule) {
          applyView(latest);
          past.current = [];
          future.current = [];
          bump((n) => n + 1);
        }
        // 最新で描き直した＝送っている間の直しも古い版の上の直しなので送らない（送れたことにして、直しを残さない）
        sq.discard();
        conflicted.current = true;
        toast(SCHEDULE_MSG.conflict);
        return;
      }
      throw e;
    }
  };
  const sq = useSaveQueue({
    send: sendDoc,
    // ページを閉じる・読み直すときは keepalive で送る（大きすぎるときは送らずにブラウザの確かめ）
    keepalive: () => {
      const body = JSON.stringify(bodyOf(docRef.current));
      return body.length > 60_000 ? null : { url: `/api/v1${url}`, method: "PUT", body };
    },
  });
  /** 上の帯の保存の様子（スマホの工程表も同じもの。409 で最新を読み込んだ直後は何も出さない） */
  const status: { k: "idle" | "saving" | "saved" | "error"; msg?: string } =
    sq.state.kind === "error" ? { k: "error", msg: sq.state.msg } : sq.state.kind === "saved" && conflicted.current ? { k: "idle" } : { k: sq.state.kind };
  const saveSoon = (ms: number) => {
    conflicted.current = false;
    sq.mark(ms === 0);
  };
  /** 送っていない変更を送り終えるまで待つ（見積・雛形から足す前に。送れなければ false） */
  const settle = sq.settle;

  /** 変える（そろえてから置く）。key が同じ続けての変更（2秒以内）は元に戻すの1回ぶん */
  const commit = (next: EditDoc, how: Save) => {
    const cur = docRef.current;
    const now = Date.now();
    const same = how.key && lastKey.current?.key === how.key && now - lastKey.current.at < 2000;
    if (!same) {
      past.current.push(cur);
      if (past.current.length > HISTORY) past.current.shift();
      future.current = [];
    }
    lastKey.current = how.key ? { key: how.key, at: now } : null;
    // 消した行・工程でなくなった行は止めた工程から外し、止める工程が0になった中止は消す（API も同じ tidyStops。#105）
    const stops = tidyStops(next.stops, next.rows);
    // 日ごとの休み・作業（#293）も消した行・{task}でなくした行の分を外す。
    // #296（docs/102 §5.9）：開始を休みの指定のある日に置いたらその指定を外し（開始が後ろへ逃げない）、そろえたあと範囲の外の指定を消す
    const rowDays0 = releaseStartRowDays(cur.rows, next.rows, tidyRowDays(next.rowDays, next.rows));
    const c0 = calOf({ ...next, stops, rowDays: rowDays0 });
    const n = normalizeSchedule({ rows: next.rows, links: next.links }, c0);
    const rowDays = pruneRowDays(rowDays0, n.rows);
    const c = rowDays.length === rowDays0.length ? c0 : calOf({ ...next, stops, rowDays });
    // 遅れが無くなった工程は「このまま」で覚えた日数を 0 に戻す（次に遅れたらまた知らせる）
    // 日のメモ（#299）：消した行・{task}でなくした行のメモを外す（範囲の外のメモは残す＝メモは日付に付く）
    const dayNotes = tidyDayNotes(next.dayNotes, n.rows);
    setDoc({ ...next, stops, rowDays, dayNotes, rows: settleDelayAck(n.rows, today, c, pctRef.current), links: n.links });
    bump((k) => k + 1);
    saveSoon(how.save === "now" ? 0 : 500);
  };
  const undo = () => {
    const prev = past.current.pop();
    if (!prev) return;
    future.current.push(docRef.current);
    lastKey.current = null;
    setDoc(prev);
    bump((n) => n + 1);
    saveSoon(0);
  };
  const redo = () => {
    const next = future.current.pop();
    if (!next) return;
    past.current.push(docRef.current);
    lastKey.current = null;
    setDoc(next);
    bump((n) => n + 1);
    saveSoon(0);
  };

  /** API で足す（見積・雛形・前の案件から）。足したあとは元に戻せる */
  const serverAdd = async (path: string, body: object): Promise<void> => {
    if (!(await settle())) throw new Error(SCHEDULE_MSG.saveFailed);
    const before = docRef.current;
    try {
      const v = await api<ScheduleView>("POST", `${url}/${path}`, { baseVersion: version.current, ...body });
      applyView(v);
      past.current.push(before);
      if (past.current.length > HISTORY) past.current.shift();
      future.current = [];
      lastKey.current = null;
      bump((n) => n + 1);
      sq.setState({ kind: "saved", at: "" });
      setAsk(null);
      setBlank(false);
      const added = v.schedule!.rows.filter((r) => !before.rows.some((b) => b.id === r.id));
      toast(SCHEDULE_MSG.added(added.length), { label: "元に戻す", onClick: undo });
      const first = added.find((r) => r.start);
      if (first) revealBar(first.id);
      if (head.groupId) void qc.invalidateQueries({ queryKey: ["group", head.groupId] });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === "SCHEDULE_CONFLICT") {
        const latest = e.problem.latest as ScheduleView | undefined;
        if (latest?.schedule) applyView(latest);
        past.current = [];
        future.current = [];
        bump((n) => n + 1);
        throw new Error(SCHEDULE_MSG.conflict);
      }
      throw e;
    }
  };

  /* ---------------- 行の操作 ---------------- */
  const rows = doc.rows;
  const focusRow = (id: string, what: "name" | "bar" = "name") =>
    requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(`[data-row-id="${id}"]`);
      const el = what === "bar" ? document.querySelector<HTMLElement>(`[data-bar-id="${id}"]`) : row?.querySelector<HTMLElement>("input");
      (el ?? row)?.focus();
      el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
  /** その行のバーが見える所まで横に流す（見えていれば動かさない。左の表の下に隠れない位置へ） */
  const revealBar = (id: string) =>
    requestAnimationFrame(() => {
      const sc = scroller.current;
      const el = sc?.querySelector<HTMLElement>(`[data-bar-id="${id}"]`);
      if (!sc || !el) return;
      const x0 = el.offsetLeft;
      const x1 = x0 + el.offsetWidth;
      const v0 = sc.scrollLeft;
      const v1 = v0 + sc.clientWidth - left;
      if (x0 >= v0 && x1 <= v1) return;
      sc.scrollLeft = Math.max(0, x0 - 3 * PX[viewRef.current]);
    });
  const insertAt = (at: number, kind: ScheduleRowKind) => {
    const d = docRef.current;
    const id = crypto.randomUUID();
    const start = kind === "task" || kind === "milestone" ? defaultStartAt(d.rows, at, today, cal) : null;
    let next = [...d.rows.slice(0, at), newRow(kind, id, start), ...d.rows.slice(at)];
    // たたんだ見出しの中に足したら、見出しをひらく
    const h = headingIndexOf(next, at);
    if (kind !== "heading" && h >= 0 && next[h]!.collapsed) next = next.map((r, j) => (j === h ? { ...r, collapsed: false } : r));
    commit({ ...d, rows: next }, { save: "now" });
    setSelectedId(id);
    focusRow(id);
    if (start) revealBar(id);
  };
  /** 見出しの行から見出し・メモ・節目を足すときは塊の下（見出しの中を割らない）。工程は見出しのすぐ下（中の最初） */
  const insertBelow = (i: number, kind: ScheduleRowKind) => {
    const r = docRef.current.rows[i]!;
    insertAt(r.kind === "heading" && kind !== "task" ? blockOf(docRef.current.rows, i)[1] : i + 1, kind);
  };
  const move = (i: number, dir: -1 | 1) => {
    const d = docRef.current;
    const id = d.rows[i]!.id;
    const next = moveRow(d.rows, i, dir);
    if (next.every((r, k) => r.id === d.rows[k]!.id)) return;
    commit({ ...d, rows: next }, { save: "now" });
    focusRow(id, document.activeElement?.closest("[data-bar-id]") ? "bar" : "name");
  };
  /** 複製：金額・見積のつながりは写さない（#101。出来高を二重に数えない） */
  const dup = (i: number) => {
    const d = docRef.current;
    const r = d.rows[i]!;
    const [a, b] = r.kind === "heading" ? blockOf(d.rows, i) : [i, i + 1];
    const copies = d.rows.slice(a, b).map((x) => copyRow(x, crypto.randomUUID()));
    commit({ ...d, rows: [...d.rows.slice(0, b), ...copies, ...d.rows.slice(b)] }, { save: "now" });
    setSelectedId(copies[0]!.id);
  };
  const doRemove = (i: number, mode: "row" | "block") => {
    const d = docRef.current;
    const r = d.rows[i]!;
    commit({ ...d, ...removeRows(d, i, mode) }, { save: "now" });
    setSelectedId(null);
    toast(SCHEDULE_MSG.deleted(r.name), { label: "元に戻す", onClick: undo });
  };
  const remove = (i: number) => {
    const d = docRef.current;
    const r = d.rows[i];
    if (!r) return;
    if (r.kind === "heading") {
      const [a, b] = blockOf(d.rows, i);
      if (b - a - 1 > 0) return setAsk({ t: "heading", index: i, n: b - a - 1, name: r.name });
    }
    doRemove(i, "row");
  };
  const registerVendor = async (name: string): Promise<string | null> => {
    try {
      const p = await api<{ id: string }>("POST", "/partners", { name, kind: "corporate", partnerType: "vendor" });
      void candsQ.refetch();
      return p.id;
    } catch (e) {
      toast(messageOf(e), undefined, "error");
      return null;
    }
  };
  const menuItems = (i: number): RowMenuItem[] => {
    const r = rows[i];
    if (!r) return [];
    const dated = r.kind === "task" || r.kind === "milestone";
    const task = r.kind === "task";
    // 区切り線で分ける段（#298、docs/102 §2.4.2）。出ない項目の段は詰める（空の段は線も出さない）
    const groups: RowMenuItem[][] = [
      // 追加
      [
        { label: t("上に{task}追加"), onSelect: () => insertAt(i, "task"), testId: "sch-mi-add-above" },
        { label: t("下に{task}追加"), onSelect: () => insertBelow(i, "task"), testId: "sch-mi-add-below" },
        { label: "見出し追加", onSelect: () => insertBelow(i, "heading"), testId: "sch-mi-add-heading" },
        { label: "メモ追加", onSelect: () => insertBelow(i, "memo"), testId: "sch-mi-add-memo" },
        { label: "節目追加", onSelect: () => insertBelow(i, "milestone"), testId: "sch-mi-add-milestone" },
      ],
      [
        { label: "上へ", onSelect: () => move(i, -1), testId: "sch-mi-up" },
        { label: "下へ", onSelect: () => move(i, 1), testId: "sch-mi-down" },
      ],
      // 日付：開始・日数（開始か日数の列を隠したとき。#296。節目は開始。見出しも期間を持てる＝#291）・前の{task}・後続と一括移動
      [
        ...((!cols.start || !cols.days) && (dated || r.kind === "heading")
          ? [{ label: r.kind === "milestone" ? SCHEDULE_MSG.rowDateItem : SCHEDULE_MSG.rowDatesItem, onSelect: () => setAsk({ t: "rowFields", id: r.id, mode: "dates" }), testId: "sch-mi-dates" }]
          : []),
        ...(dated
          ? [
              { label: t(SCHEDULE_MSG.predsItem), onSelect: () => setAsk({ t: "preds", id: r.id }), testId: "sch-mi-preds" },
              { label: SCHEDULE_MSG.shiftItem, onSelect: () => setAsk({ t: "shift", id: r.id }), testId: "sch-mi-shift" },
            ]
          : []),
      ],
      // 工程ごとに「休日も作業」（9/30 射場さん。土日祝も日数に数える）・「随時」（#291。期間の中でときどき作業。バーは点線・遅れに出さない）・
      // 「雨天も作業」（#105。中止のダイアログで最初から外す。現場力だけ＝営業力は雨を前に出さない。#142 段5 No.127）。
      // S-00009：✓ の付く項目（menuitemcheckbox。見積の「{takeoffSheet}を付ける」と同じ形）
      task
        ? [
            { label: SCHEDULE_MSG.offWorkItem, checked: !!r.workOffDays, onSelect: () => ops.update(r.id, { workOffDays: !r.workOffDays }, { save: "now" }), testId: "sch-mi-offwork" },
            { label: SCHEDULE_MSG.intermittent, checked: !!r.intermittent, onSelect: () => ops.update(r.id, { intermittent: !r.intermittent }, { save: "now" }), testId: "sch-mi-intermittent" },
            ...(showsRain() ? [{ label: SCHEDULE_MSG.rainOkItem, checked: !!r.rainOk, onSelect: () => ops.update(r.id, { rainOk: !r.rainOk }, { save: "now" }), testId: "sch-mi-rainok" }] : []),
          ]
        : [],
      // 中止と日ごとの休み・作業（#293：バーの日をクリックするのと同じことを暦で＝ドラッグしない方法・タブレットとスマホの編集）。
      // その{task}が止まっている日（{task}を選んだ中止）ごとに「中止解除（10/1）」
      task && r.start
        ? [
            { label: SCHEDULE_MSG.stopThisRow, onSelect: () => setAsk({ t: "stops", rowId: r.id }), testId: "sch-mi-stop" },
            ...dateRuns(rowStopDates(doc.stops, r.id)).map((run) => ({
              label: SCHEDULE_MSG.stopRowRemove(datesText(run.from, run.to)),
              onSelect: () => unstopRow(r.id, run.dates),
              testId: "sch-mi-unstop",
            })),
            { label: SCHEDULE_MSG.rowDaysItem, onSelect: () => setAsk({ t: "rowDays", id: r.id }), testId: "sch-mi-rowdays" },
            // 日のメモ（#299）：開始〜終わりの日ごとに文の欄（タブレット・スマホ・キーボード＝右クリックしない方法）
            { label: SCHEDULE_MSG.dayNotesItem, onSelect: () => setAsk({ t: "dayNotes", id: r.id }), testId: "sch-mi-daynotes" },
          ]
        : [],
      // 状況（進捗）の列を隠したとき（#296）・人の列を出していないとき（#297。既定）：ドラッグしない方法（スマホの ⋮ も同じ）
      task
        ? [
            ...(!cols.state ? [{ label: pctRef.current ? SCHEDULE_MSG.rowProgressItem : SCHEDULE_MSG.rowStateItem, onSelect: () => setAsk({ t: "rowFields", id: r.id, mode: "state" }), testId: "sch-mi-state" }] : []),
            ...(!showCrew ? [{ label: SCHEDULE_MSG.rowCrewItem, onSelect: () => setAsk({ t: "rowFields", id: r.id, mode: "crew" }), testId: "sch-mi-crew" }] : []),
          ]
        : [],
      // 担当が取引先（業者・{顧客}）の工程・節目：リンクを送る（#103 §6.2 の12）
      r.assignee?.type === "partner"
        ? [{ label: PARTNER_LINK_MSG.menuSendLink(assigneeLabel(r.assignee, candsQ.data)), onSelect: () => setLinkFor({ id: (r.assignee as { id: string }).id, name: assigneeLabel(r.assignee, candsQ.data) }), testId: "sch-mi-link" }]
        : [],
      [{ label: "複製", onSelect: () => dup(i), testId: "sch-mi-dup" }],
      [{ label: "削除", onSelect: () => remove(i), testId: "sch-mi-delete", danger: true }],
    ];
    return menuGroups(groups);
  };
  const ops: GanttOps = {
    update: (id, patch, how) => {
      const d = docRef.current;
      commit(
        {
          ...d,
          rows: d.rows.map((r) => {
            if (r.id !== id) return r;
            // ％で入力した{task}（#292）：最初に 1 以上にした日を着手日、100 にした日を完了日に（0 に戻すと消す）
            if (r.kind === "task" && patch.progress !== undefined && patch.actualStart === undefined) return setProgressPct({ ...r, ...patch, progress: r.progress }, patch.progress ?? 0, today);
            return { ...r, ...patch };
          }),
        },
        how,
      );
    },
    stateClick: (id) => changeState(id),
    replace: (next, how) => commit({ ...docRef.current, rows: next.rows, links: next.links }, how),
    remove,
    menuItems,
    registerVendor,
    defaultStart: (i) => defaultStartAt(docRef.current.rows, i, today, cal),
    reveal: (id) => revealBar(id),
    stopClick: (date) => {
      const d = docRef.current;
      const next = d.stops.filter((x) => x.date !== date);
      commit({ ...d, stops: next }, { save: "now" });
      toast(SCHEDULE_MSG.stopRemoved(spanText(date, date)), { label: "元に戻す", onClick: undo });
    },
    dayToggle: (rowId, date) => toggleDay(rowId, date),
    noteEdit: (tg) => setNotePop(tg),
    stopOpen: (date, rowId) => setAsk({ t: "stops", date, ...(rowId ? { rowId } : {}) }),
    siteOff: (date) => {
      // 日付の見出しのクリック（#293・#296）：平日 → {siteShort}の休み ⇄ 解除・休みの日 → {siteShort}の作業日 ⇄ 解除。
      // 中止の日は Gantt が中止のポップを開く（ここには来ない）
      const d = docRef.current;
      const res = toggleSiteDay(d.stops, d.workDays, date, calOf(d));
      if (res.action === "stop") return;
      const before = d.rows;
      commit({ ...d, stops: res.stops, workDays: res.workDays }, { save: "now" });
      const label = mdwJaOf(date, cal.holidays);
      const n = changedTaskCount(before, docRef.current.rows);
      const text =
        res.action === "offAdded"
          ? SCHEDULE_MSG.siteOffDone(label, n)
          : res.action === "offRemoved"
            ? SCHEDULE_MSG.siteOffRemoved(label)
            : res.action === "workAdded"
              ? SCHEDULE_MSG.siteWorkDone(label, n)
              : SCHEDULE_MSG.siteWorkRemoved(label);
      toast(text, { label: "元に戻す", onClick: undo });
    },
    leftChange: (px, done) => {
      const v = Math.max(NAME_MANUAL_MIN, Math.min(nameMax, Math.round(px - FIXED_W - extraW)));
      setNamePref(v);
      if (done) writeName(v);
    },
    // 境のダブルクリック（#296）：自動幅
    leftReset: () => {
      setNamePref("auto");
      writeName("auto");
    },
    conflict: (rowId, anchor) => setConflictPop({ rowId, anchor }),
    report: (rowId, anchor) => setReportFor({ rowId, anchor }),
    photos: (rowId) => photosOfRow(rowId),
  };
  /**
   * 状況を変える（#292、docs/102 §4。％ OFF の会社）：表のセルは 未着手 → 着手（着手日＝今日）→ 完了（完了日＝今日・進み 100）→ 未着手（両方を消す）。
   * スマホのシートは押したボタンの状況へ。トースト「「{task}名」を着手にしました」＋元に戻す（Ctrl＋Z も）
   */
  const changeState = (id: string, to?: TaskState) => {
    const d = docRef.current;
    const r = d.rows.find((x) => x.id === id);
    if (!r || r.kind !== "task") return;
    const cur = taskStateOf(r);
    const next = to ?? nextTaskState(cur);
    if (next === cur) return;
    commit({ ...d, rows: d.rows.map((x) => (x.id === id ? setTaskState(x, next, today) : x)) }, { save: "now" });
    toast(SCHEDULE_MSG.stateSet(r.name, TASK_STATE_LABEL[next]), { label: "元に戻す", onClick: undo });
  };
  /** その{task}のその日を 休み ⇄ 作業（#293。バーの日のクリックと ⋮ の暦）。中止・{siteShort}の休みの日は変えない。元に戻せる */
  const toggleDay = (rowId: string, date: string) => {
    const d = docRef.current;
    const r = d.rows.find((x) => x.id === rowId);
    if (!r) return;
    const c = calOf(d);
    const res = toggleRowDay(d.rowDays, r, date, c);
    if (!res) return;
    commit({ ...d, rowDays: res.rowDays }, { save: "now" });
    const label = mdwJaOf(date, c.holidays);
    toast(res.work ? SCHEDULE_MSG.rowDayWorkDone(label) : SCHEDULE_MSG.rowDayOffDone(label), { label: "元に戻す", onClick: undo });
  };
  /** 日のメモを保存（#299。空なら削除）。トースト「11/14 のメモを保存しました」＋元に戻す */
  const saveNote = (rowId: string | null, date: string, text: string) => {
    const d = docRef.current;
    const had = d.dayNotes.some((x) => x.rowId === rowId && x.day === date);
    const t0 = text.trim();
    if (!t0 && !had) return;
    commit({ ...d, dayNotes: setDayNote(d.dayNotes, rowId, date, t0) }, { save: "now" });
    toast(t0 ? SCHEDULE_MSG.dayNoteSaved(mdOf(date)) : SCHEDULE_MSG.dayNoteDeleted(mdOf(date)), { label: "元に戻す", onClick: undo });
  };
  /** 「10/17(土) から始める」（#102）：その工程を相手の終わりの次の稼働日から。後ろも押す。元に戻せる */
  const startFrom = (rowId: string, date: string) => {
    const d = docRef.current;
    const r = d.rows.find((x) => x.id === rowId);
    if (!r) return;
    const next = startAfter(d, rowId, date, calOf(d));
    setConflictPop(null);
    commit({ ...d, rows: next.rows, links: next.links }, { save: "now" });
    const nr = docRef.current.rows.find((x) => x.id === rowId);
    toast(SCHEDULE_MSG.startedFrom(r.name, mdwOf(nr?.start ?? date)), { label: "元に戻す", onClick: undo });
    revealBar(rowId);
  };

  /* ---------------- 協力会社からの報告（#103） ---------------- */
  /** 反映する：保存待ちの変更を先に送る → API が工程表を直して版を＋1 → 読み直す（元に戻すの控えは消す＝API で直したので） */
  const applyReportNow = async (r: ScheduleReport) => {
    if (!(await settle())) throw new Error(SCHEDULE_MSG.saveFailed);
    await api<ScheduleReportApplied>("POST", `/schedule/reports/${r.id}/apply`);
    const v = await api<ScheduleView>("GET", url);
    applyView(v);
    past.current = [];
    future.current = [];
    bump((n) => n + 1);
    refreshReports();
    toast(PARTNER_LINK_MSG.applied);
  };
  /** 見送ったあと：確かめ待ちだけ読み直す（工程表は変わらない） */
  const reloadReports = async () => {
    setReports(await api<ScheduleReport[]>("GET", `/schedule/reports?status=pending&${head.groupId ? `group=${head.groupId}` : `schedule=${head.scheduleId}`}`));
    refreshReports();
    toast(PARTNER_LINK_MSG.dismissed);
  };
  const photosOfRow = (rowId: string) => {
    const xs = photos.filter((p) => p.rowId === rowId);
    if (xs.length) setViewer({ ids: xs.map((p) => p.id), i: 0, caps: xs.map((p) => PARTNER_LINK_MSG.photoBy(p.partnerName, mdHmJst(p.sentAt)) + (p.note ? `「${p.note}」` : "")) });
  };
  const reportMarks = useMemo(() => {
    const photoCount = new Map<string, number>();
    for (const p of photos) photoCount.set(p.rowId, (photoCount.get(p.rowId) ?? 0) + 1);
    return { reports: new Set(reports.flatMap((r) => (r.row ? [r.row.id] : []))), photos: photoCount };
  }, [reports, photos]);

  /* ---------------- 中止・まとめてずらす・遅れ ---------------- */
  const addStopsTo = (next: ScheduleStop[], from: string, to: string, target: StopTarget) => {
    const d = docRef.current;
    const before = d.rows;
    commit({ ...d, stops: next }, { save: "now" });
    const n = changedTaskCount(before, docRef.current.rows);
    setAsk(null);
    // 「10/8・10/9 に 2工程を止めました。n工程が延びました」／現場全体は「10/1 を中止にしました。n工程が延びました」
    toast(target.scope === "rows" ? SCHEDULE_MSG.stopDoneRows(datesText(from, to), target.rowIds.length, n) : SCHEDULE_MSG.stopDone(spanText(from, to), n), {
      label: "元に戻す",
      onClick: undo,
    });
  };
  /** その工程だけ中止から外す（⋮「10/1 の中止を外す」） */
  const unstopRow = (rowId: string, dates: string[]) => {
    const d = docRef.current;
    const r = d.rows.find((x) => x.id === rowId);
    commit({ ...d, stops: removeRowFromStops(d.stops, rowId, dates) }, { save: "now" });
    toast(SCHEDULE_MSG.stopRowRemoved(r?.name ?? "", datesText(dates[0]!, dates[dates.length - 1]!)), { label: "元に戻す", onClick: undo });
  };
  const removeStops = (dates: string[]) => {
    const d = docRef.current;
    commit({ ...d, stops: d.stops.filter((x) => !dates.includes(x.date)) }, { save: "now" });
    toast(SCHEDULE_MSG.stopRemoved(spanText(dates[0]!, dates[dates.length - 1]!)), { label: "元に戻す", onClick: undo });
  };
  const doShiftFrom = (from: string, n: number) => {
    const d = docRef.current;
    const r = shiftFrom(d, from, n, calOf(d));
    setAsk(null);
    if (!r.moved) return toast(SCHEDULE_MSG.shiftFromNone);
    commit({ ...d, rows: r.doc.rows, links: r.doc.links }, { save: "now" });
    toast(SCHEDULE_MSG.shiftFromDone(r.moved, n), { label: "元に戻す", onClick: undo });
  };
  /** 「このまま」（2工程以上なら全部。#105） */
  const keep = (ids: string[]) => {
    const d = docRef.current;
    const set = new Set(ids);
    commit({ ...d, rows: d.rows.map((r) => (set.has(r.id) ? keepDelay(r, today, calOf(d), pctRef.current) : r)) }, { save: "now" });
  };
  /** 着手の遅れの「このまま」（#292。％ OFF の会社だけ） */
  const keepStart = (ids: string[]) => {
    const d = docRef.current;
    const set = new Set(ids);
    commit({ ...d, rows: d.rows.map((r) => (set.has(r.id) ? keepStartDelay(r, today, calOf(d)) : r)) }, { save: "now" });
  };
  const extend = (id: string) => {
    const d = docRef.current;
    const r = d.rows.find((x) => x.id === id);
    if (!r) return;
    const n = shownDelayOf(r, today, calOf(d), pctRef.current);
    commit({ ...d, rows: d.rows.map((x) => (x.id === id ? extendByDelay(x, today, calOf(d), pctRef.current) : x)) }, { save: "now" });
    toast(SCHEDULE_MSG.delayExtended(r.name, n), { label: "元に戻す", onClick: undo });
  };

  /* ---------------- キー（画面全体） ---------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const typing = !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
      if (ask || document.querySelector(".dim")) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !typing && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (mod && !typing && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
      } else if (!typing && !readOnly && (e.key === "Delete" || e.key === "Backspace") && selectedId && !document.querySelector(".sch-link.sel")) {
        const i = docRef.current.rows.findIndex((r) => r.id === selectedId);
        if (i >= 0) {
          e.preventDefault();
          remove(i);
        }
      } else if (e.key === "Escape" && selectedId && !typing) setSelectedId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  /* ---------------- 時間軸（範囲・横の位置） ---------------- */
  const px = view === "day" && dayPx ? dayPx : PX[view];
  const range = useMemo(() => rangeOf(doc.rows, today, view), [doc.rows, today, view]);
  // 時間軸の物差し（#291 §2.1.1：旬は1日の幅が旬の日数で変わる）
  const sc = useMemo(() => scaleOf(view, range.start, range.days, px), [view, range.start, range.days, px]);
  const ixOf = (ymd: string) => dayNo(ymd) - dayNo(range.start);
  const [leftDay, setLeftDay] = useState<string>(range.start);
  const prev = useRef<{ start: string; px: number; leftDay: string } | null>(null);
  // 範囲の最初の日・1日の幅が変わっても、見えている最初の日を動かさない
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const p = prev.current;
    if (!p) {
      // はじめ：いちばん早い日（無ければ今日）の2日前から
      const firsts = doc.rows.map((r) => r.start).filter((d): d is string => !!d).sort();
      const first = firsts[0] ?? today;
      // 旬・月の表示（#291 §2.1.1）は月の1日から見せる（日の表示は2日前から）
      el.scrollLeft = Math.max(0, view === "day" ? sc.x(ixOf(first) - 2) : sc.x(ixOf(monthStartOf(first))));
    } else if (p.start !== range.start || p.px !== px) {
      el.scrollLeft = Math.max(0, sc.x(ixOf(view === "day" ? p.leftDay : monthStartOf(p.leftDay))));
    }
    prev.current = { start: range.start, px, leftDay: ymdOfDayNo(dayNo(range.start) + sc.idxAt(el.scrollLeft)) };
    setLeftDay(prev.current.leftDay);
  }, [range.start, px]); // eslint-disable-line react-hooks/exhaustive-deps
  const onScroll = () => {
    const el = scroller.current;
    if (!el || !prev.current) return;
    const d = ymdOfDayNo(dayNo(range.start) + sc.idxAt(el.scrollLeft));
    prev.current.leftDay = d;
    setLeftDay(d);
  };
  const vwPx = Math.max(px, (scroller.current?.clientWidth ?? 900) - left);
  const rightDay = ymdOfDayNo(dayNo(range.start) + Math.max(ixOf(leftDay), sc.idxAt(sc.x(ixOf(leftDay)) + vwPx) - 1));
  const scrollBy = (days: number) => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    scroller.current?.scrollBy({ left: days * px, behavior: reduce ? "auto" : "smooth" });
  };
  const toToday = () => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    scroller.current?.scrollTo({ left: Math.max(0, view === "day" ? sc.x(ixOf(today) - 3) : sc.x(ixOf(monthStartOf(today)))), behavior: reduce ? "auto" : "smooth" });
  };
  // 2本指で拡大・縮小（日の表示に切り替えて 12〜48px）
  const onZoom = useCallback((n: number) => {
    setView("day");
    setDayPx(n);
  }, []);
  usePinchZoom(scroller, px, onZoom, left);

  /* ---------------- ?row=<id>（全体の工程表・重なりの吹き出しから。#102） ---------------- */
  const [sp, setSp] = useSearchParams();
  const wantRow = sp.get("row");
  useEffect(() => {
    if (!wantRow || phone) return;
    const r = docRef.current.rows.find((x) => x.id === wantRow);
    const t = window.setTimeout(() => {
      if (r) {
        setSelectedId(r.id);
        const row = document.querySelector<HTMLElement>(`[data-row-id="${r.id}"]`);
        row?.scrollIntoView({ block: "center", inline: "nearest" });
        if (r.start) revealBar(r.id);
        const mark = row?.querySelector<HTMLElement>('[data-testid="sch-overlap"]');
        if (mark && conflictOf.has(r.id)) setConflictPop({ rowId: r.id, anchor: mark });
      }
      // 開いたら ?row= は外す（再読み込みで何度も開かない）
      const next = new URLSearchParams(sp);
      next.delete("row");
      setSp(next, { replace: true });
    }, 80);
    return () => window.clearTimeout(t);
  }, [wantRow]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- 担当の候補・雛形・うつす元 ---------------- */
  const asgParam = head.groupId ? `group=${head.groupId}` : `schedule=${head.scheduleId}`;
  const candsQ = useQuery({ queryKey: ["schedule-assignees", asgParam], queryFn: () => api<ScheduleAssignees>("GET", `/schedule/assignees?${asgParam}`), staleTime: 60_000 });
  candsForWidth.current = candsQ.data;
  const empty = rows.length === 0 && !blank;
  const tplQ = useQuery({ queryKey: ["schedule-templates"], queryFn: () => api<ScheduleTemplateItem[]>("GET", "/schedule-templates"), enabled: empty, staleTime: 0 });
  const srcQ = useQuery({ queryKey: ["schedule-sources", head.groupId ?? "", ""], queryFn: () => api<ScheduleSource[]>("GET", `/schedule/sources${head.groupId ? `?exclude=${head.groupId}` : ""}`), enabled: empty, staleTime: 0 });
  const estimates = head.estimates;

  /* ---------------- 数字の枠・遅れ ---------------- */
  // #292：％ OFF（既定）は 進み具合＝完了した{task}の日数・遅れ＝終わりを過ぎて未完了／着手の遅れ・出来高＝完了した{task}の金額。「予定 n%」は％ ON だけ
  const brief = useMemo(() => scheduleBriefOf(doc.rows, undefined, pct), [doc.rows, pct]);
  const nextMs = nextMilestoneOf(brief, today);
  const lastMs = lastMilestoneOf(brief);
  const planned = useMemo(() => (pct ? plannedProgressOf(doc.rows, today, cal) : null), [doc.rows, today, cal, pct]);
  const delayed = useMemo(() => delayedRows(doc.rows, today, cal, pct), [doc.rows, today, cal, pct]);
  const startDelayed = useMemo(() => startDelayedRows(doc.rows, today, cal, pct), [doc.rows, today, cal, pct]);
  const delays = useMemo(() => new Map(delayed.map((x) => [x.row.id, x.delay])), [delayed]);
  const earned = useMemo(() => earnedOf(doc.rows, pct), [doc.rows, pct]);
  // 見込み：遅れを入れてそろえた最後の節目（無ければ終わり）の日
  const forecast = useMemo(() => {
    const lastDate = lastMs?.date ?? brief.end;
    if (!delayed.length || !lastDate) return null;
    const p = projectOf(doc, today, cal, pct);
    const target = lastMs
      ? [...doc.rows].reverse().find((r) => r.kind === "milestone" && r.start === lastMs.date)
      : null;
    const pd = target ? p.rows.find((r) => r.id === target.id)?.start : scheduleBriefOf(p.rows).end;
    if (!pd || pd <= lastDate) return null;
    return { date: pd, days: Math.max(1, workDaysBetween(addDays(lastDate, 1), pd, cal)) };
  }, [doc, delayed, today, cal, lastMs, brief.end, pct]);

  const L = useLabels();
  const hasAmount = rows.some((r) => r.kind === "task" && r.amount != null);
  const offText = offTextOf(doc, companyOff);
  // 出力（#104、docs/39 §7.3 → #298、docs/102 §2.4.2）：操作の列の「一括移動」の右のボタン（全体の{schedule}の「出力」と同じ部品・同じ文言）。
  // 空の{schedule}は押せない（title「{task}がありません」）。閲覧のみ・タブレットの見るだけ・スマホでも出す
  const exportButton = (
    <button
      type="button"
      className="btn"
      onClick={() => setExportOpen(true)}
      disabled={!rows.length}
      title={rows.length ? undefined : t(SCHEDULE_EXPORT_MSG.empty)}
      data-testid="sch-export-btn"
    >
      <CrIcon k="download" />
      {SCHEDULE_EXPORT_MSG.button}
    </button>
  );
  const exportTarget = useMemo(
    (): GroupExportTarget => ({
      kind: "group",
      groupId: head.groupId,
      scheduleId: head.scheduleId,
      subject: head.name,
      addressee: head.clientName ? scheduleAddressee(head.clientName, head.clientHonorific) : "",
      staff: head.staffName,
      schedule: { rows: doc.rows, links: doc.links, offWeekdays: doc.offWeekdays, holidayOff: doc.holidayOff, useCompanyOffDays: doc.useCompanyOffDays, stops: doc.stops, rowDays: doc.rowDays, workDays: doc.workDays },
      holidays,
      companyOffDays: companyOff,
      progressPct: pct,
    }),
    [head.groupId, head.scheduleId, head.name, head.clientName, head.clientHonorific, head.staffName, doc, holidays, companyOff, pct],
  );
  // 操作の列は1段（#105 の2）：「休日」「金額」「表示設定」は ⋮ へ。#298：出力は操作の列のボタン・表の列／バーの日付／表の幅は「表示設定」の窓に1つ
  const moreItems: RowMenuItem[] = menuGroups([
    rows.length
      ? [
          ...(estimates ? [{ label: SCHEDULE_MSG.fromEstimateAddTitle, onSelect: () => setAsk({ t: "fromEstimate" }), testId: "sch-more-from-estimate" }] : []),
          { label: SCHEDULE_MSG.fromTemplateTitle, onSelect: () => setAsk({ t: "fromTemplate" }), testId: "sch-more-from-template" },
          { label: t("前の{project}から追加"), onSelect: () => setAsk({ t: "fromGroup" }), testId: "sch-more-from-group" },
          { label: SCHEDULE_MSG.templateSave, onSelect: () => setAsk({ t: "saveTemplate" }), testId: "sch-more-save-template" },
        ]
      : [],
    // 案件なしの{schedule}だけ（#291）：案件に紐づける
    head.groupId ? [] : [{ label: SCHEDULE_MSG.attach, onSelect: () => setAttachOpen(true), testId: "sch-more-attach" }],
    [
      { label: SCHEDULE_MSG.offDaysItem(offText), onSelect: () => setOffOpen(true), testId: "sch-more-offdays" },
      // 表示設定（#298）：列＝開始・日数・状況・人（#296・#297）／表の幅＝標準・広い・自動（#296）／バーの日付＝常に表示（#291 §2.1.1）
      { label: SCHEDULE_MSG.viewItem, onSelect: () => setAsk({ t: "cols" }), testId: "sch-more-display" },
      ...(hasAmount
        ? [{ label: SCHEDULE_MSG.amountColumnItem, hint: SCHEDULE_MSG.amountHead, checked: showAmount, onSelect: () => setShowAmount((v) => !v), testId: "sch-more-amount" }]
        : []),
    ],
    [{ label: SCHEDULE_MSG.scheduleDeleteTitle, onSelect: () => setAsk({ t: "deleteSchedule" }), testId: "sch-delete-schedule", danger: true }],
  ]);
  const stopRow = ask?.t === "stops" && ask.rowId ? rows.find((r) => r.id === ask.rowId) : undefined;

  // ダイアログ・吹き出し（PC とスマホで同じもの）
  const overlays = (
    <>
      {/* 出力は保存を待ってから（#179：保存してある古い中身で出さない） */}
      {exportOpen ? <ExportDialog target={exportTarget} onClose={() => setExportOpen(false)} toast={(t) => toast(t)} beforeExport={sq.ready} /> : null}
      {sq.dialog}
      {ask?.t === "heading" ? (
        <HeadingDelete
          name={ask.name}
          n={ask.n}
          onCancel={() => setAsk(null)}
          onDelete={(mode) => {
            setAsk(null);
            doRemove(ask.index, mode);
          }}
        />
      ) : null}
      {ask?.t === "preds" ? (
        <PredPicker
          doc={doc}
          id={ask.id}
          onCancel={() => setAsk(null)}
          onOk={(links) => {
            setAsk(null);
            commit({ ...docRef.current, links }, { save: "now" });
          }}
        />
      ) : null}
      {ask?.t === "shift" ? (
        <ShiftDialog
          name={rows.find((r) => r.id === ask.id)?.name ?? ""}
          onCancel={() => setAsk(null)}
          onOk={(n) => {
            setAsk(null);
            if (n) commit({ ...docRef.current, ...shiftWithSuccessors(docRef.current, ask.id, n, cal) }, { save: "now" });
          }}
        />
      ) : null}
      {ask?.t === "deleteSchedule" ? (
        <ScheduleDelete
          onCancel={() => setAsk(null)}
          onDelete={async () => {
            try {
              sq.discard(); // 消す工程表の書きかけは送らない
              await api("DELETE", url);
              setAsk(null);
              onDeleted();
            } catch (e) {
              setAsk(null);
              toast(messageOf(e), undefined, "error");
            }
          }}
        />
      ) : null}
      {ask?.t === "fromEstimate" ? (
        <FromEstimateDialog
          groupId={head.groupId ?? ""}
          hasRows={rows.length > 0}
          defaultStart={defaultStartAfter(rows, today, cal)}
          cal={cal}
          offText={offText}
          cands={candsQ.data}
          onRegister={registerVendor}
          onOffDays={() => {
            setAsk(null);
            setOffOpen(true);
          }}
          onCancel={() => setAsk(null)}
          onSubmit={(v: FromEstimateSubmit) => serverAdd("from-estimate", v)}
        />
      ) : null}
      {ask?.t === "fromTemplate" ? (
        <FromTemplateDialog defaultStart={defaultStartAfter(rows, today, cal)} onCancel={() => setAsk(null)} onSubmit={(v) => serverAdd("from-template", v)} />
      ) : null}
      {ask?.t === "fromGroup" ? (
        <FromGroupDialog groupId={head.groupId ?? ""} defaultStart={defaultStartAfter(rows, today, cal)} onCancel={() => setAsk(null)} onSubmit={(v) => serverAdd("from-group", v)} />
      ) : null}
      {ask?.t === "saveTemplate" ? (
        <SaveTemplateDialog
          defaultName={head.name}
          onCancel={() => setAsk(null)}
          onSubmit={async (name) => {
            if (!(await settle())) throw new Error(SCHEDULE_MSG.saveFailed);
            await api("POST", "/schedule-templates", head.groupId ? { name, groupId: head.groupId } : { name, scheduleId: head.scheduleId });
            void qc.invalidateQueries({ queryKey: ["schedule-templates"] });
            setAsk(null);
            toast(SCHEDULE_MSG.templateSaved(name));
          }}
        />
      ) : null}
      {ask?.t === "dayNotes" && rows.find((r) => r.id === ask.id) ? (
        <DayNotesDialog
          row={rows.find((r) => r.id === ask.id)!}
          notes={doc.dayNotes}
          holidays={cal.holidays}
          onSave={(day, text) => saveNote(ask.id, day, text)}
          onClose={() => setAsk(null)}
        />
      ) : null}
      {notePop ? (
        <DayNotePop
          key={`${notePop.rowId ?? ""}|${notePop.date}`}
          target={notePop}
          holidays={cal.holidays}
          text={doc.dayNotes.find((x) => x.rowId === notePop.rowId && x.day === notePop.date)?.text ?? ""}
          onSave={(text) => {
            setNotePop(null);
            saveNote(notePop.rowId, notePop.date, text);
          }}
          onClose={() => setNotePop(null)}
        />
      ) : null}
      {ask?.t === "stops" ? (
        <StopsDialog
          doc={doc}
          calOf={(stops) => calOf({ ...docRef.current, stops })}
          defaultDate={
            // 右クリックの「{task}中止」・見出しの「中止」（#299）：その日
            ask.date
              ? ask.date
              : // 工程の ⋮「この工程を中止…」：今日（その工程の稼働日でなければ次の稼働日。始まる前ならその工程の最初の日）
                stopRow?.start
              ? snapToWorkDay(today < stopRow.start ? stopRow.start : stopRow.end && today > stopRow.end ? stopRow.end : today, calendarForRow(stopRow, cal))
              : today
          }
          rowId={stopRow?.id}
          onCancel={() => setAsk(null)}
          onAdd={addStopsTo}
          onRemove={removeStops}
        />
      ) : null}
      {ask?.t === "shiftFrom" ? <ShiftFromDialog defaultDate={today} onCancel={() => setAsk(null)} onOk={doShiftFrom} /> : null}
      {ask?.t === "cols" ? (
        <DisplayDialog
          cols={cols}
          crew={showCrew}
          pct={pct}
          width={namePref == null ? "std" : namePref === "wide" || namePref === "auto" ? namePref : "manual"}
          barDates={barDatesAll}
          onChange={setCols}
          onCrew={setShowCrew}
          onWidth={(w) => {
            const v: NamePref = w === "std" ? null : w;
            setNamePref(v);
            writeName(v);
          }}
          onBarDates={setBarDatesAll}
          onClose={() => setAsk(null)}
        />
      ) : null}
      {ask?.t === "rowFields" && rows.find((r) => r.id === ask.id) ? (
        <RowFieldsDialog
          row={rows.find((r) => r.id === ask.id)!}
          mode={ask.mode}
          pct={pct}
          today={today}
          onDates={(patch) => {
            const r = rows.find((x) => x.id === ask.id)!;
            if (patch.start) ops.update(r.id, r.kind === "milestone" ? { start: patch.start, end: patch.start } : { start: patch.start, days: r.days ?? 1 }, { save: "now" });
            if (patch.days) ops.update(r.id, { days: patch.days }, { save: "now" });
          }}
          onState={(to) => changeState(ask.id, to)}
          onPct={(n) => ops.update(ask.id, { progress: n }, { save: "now" })}
          onCrew={(n) => ops.update(ask.id, { crew: n }, { save: "now" })}
          onClose={() => setAsk(null)}
        />
      ) : null}
      {ask?.t === "rowDays" && rows.find((r) => r.id === ask.id)?.start ? (
        <RowDaysDialog row={rows.find((r) => r.id === ask.id)!} cal={cal} onToggle={(date) => toggleDay(ask.id, date)} onClose={() => setAsk(null)} />
      ) : null}
      {offOpen ? (
        <OffDaysDialog
          offWeekdays={doc.offWeekdays}
          holidayOff={doc.holidayOff}
          useCompanyOffDays={doc.useCompanyOffDays}
          companyOff={companyOff}
          workDays={doc.workDays}
          cal={cal}
          onClose={() => setOffOpen(false)}
          onChange={(patch) => commit({ ...docRef.current, ...patch }, { save: "now" })}
        />
      ) : null}
      {attachOpen ? (
        <AttachDialog
          scheduleId={head.scheduleId}
          title={head.title}
          partnerId={head.partnerId}
          onCancel={() => setAttachOpen(false)}
          onAttached={async (v) => {
            // 送っていない変更を先に送ってから移る（紐づけは版を＋1 する）
            sq.discard();
            setAttachOpen(false);
            void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
            const gid = v.schedule!.groupId!;
            qc.setQueryData(["schedule", gid], v);
            nav(`/groups/${gid}/schedule`, { replace: true, state: { notice: SCHEDULE_MSG.attached(head.title) } });
          }}
        />
      ) : null}
      {linkFor ? <PartnerLinkDialog partnerId={linkFor.id} partnerName={linkFor.name} onClose={() => setLinkFor(null)} toast={(t) => toast(t)} /> : null}
      {viewer ? <PhotoViewer ids={viewer.ids} index={viewer.i} caption={(i) => viewer.caps[i] ?? ""} onClose={() => setViewer(null)} /> : null}
      {conflictPop && conflictOf.get(conflictPop.rowId) ? (
        <ConflictPop
          conflict={conflictOf.get(conflictPop.rowId)!}
          startDate={(() => {
            const r = rows.find((x) => x.id === conflictPop.rowId);
            return r ? conflictStartDate(conflictOf.get(conflictPop.rowId)!, r, cal) : null;
          })()}
          anchor={conflictPop.anchor}
          readOnly={readOnly}
          onClose={() => setConflictPop(null)}
          onStart={(date) => startFrom(conflictPop.rowId, date)}
        />
      ) : null}
    </>
  );

  // 協力会社からの報告（#103 §6.3.4）。#171 から上の藍の帯の見出しの右のチップ（押すと吹き出し）
  const reportNotice = (
    <ReportsNotice reports={reports} onApply={applyReportNow} onChanged={() => void reloadReports()} readOnly={readOnly} openFor={reportFor} onOpenDone={() => setReportFor(null)} />
  );

  // スマホ（768px 未満）：E2・E3・E4（#102 §5.3.3）
  if (phone) {
    const phoneMore: RowMenuItem[] = [
      { label: "見出し追加", onSelect: () => insertAt(docRef.current.rows.length, "heading"), testId: "sch-more-add-heading" },
      { label: "メモ追加", onSelect: () => insertAt(docRef.current.rows.length, "memo"), testId: "sch-more-add-memo" },
      { label: "節目追加", onSelect: () => insertAt(docRef.current.rows.length, "milestone"), testId: "sch-more-add-milestone" },
      // 表示設定（表の列・表の幅・バーの日付）と金額の列は PC・タブレットの表だけ（スマホの表には無い）
      ...moreItems.filter((m) => m.testId !== "sch-more-amount" && m.testId !== "sch-more-display").map((m, k) => (k === 0 ? { ...m, divider: true } : m)),
    ];
    return (
      <>
        <PhoneSchedule
          title={head.name}
          back={back}
          backLabel={back.label === head.name ? L.project : back.label}
          doc={doc}
          cal={cal}
          today={today}
          edit={editOn}
          onEdit={setEditOn}
          ops={ops}
          cands={candsQ.data}
          delays={delays}
          conflicts={conflictOf}
          progress={brief.progress}
          pct={pct}
          crew={showCrew}
          done={{ done: brief.doneCount, total: brief.taskCount }}
          onState={(id, to) => changeState(id, to)}
          lastMs={lastMs}
          end={brief.end}
          status={status}
          moreItems={phoneMore}
          exportButton={exportButton}
          onAddTask={() => insertAt(docRef.current.rows.length, "task")}
          onProgress={(id, n) => {
            const r = docRef.current.rows.find((x) => x.id === id);
            if (!r || (r.progress ?? 0) === n) return;
            ops.update(id, { progress: n }, { save: "now" });
            toast(SCHEDULE_MSG.progressSet(n), { label: "元に戻す", onClick: undo });
          }}
          onConflict={(rowId, anchor) => setConflictPop({ rowId, anchor })}
          dayPx={dayPx}
          onZoom={setDayPx}
          notice={reportNotice}
          dayNotes={doc.dayNotes}
        />
        {overlays}
        {back.dialog}
        {toastNode}
      </>
    );
  }

  return (
    <div className="sch-page full">
      <ScheduleBand
        head={head}
        back={back}
        backLabel={back.label === head.name ? L.project : back.label}
        rows={doc.rows}
        progress={brief.progress}
        pct={pct}
        done={{ done: brief.doneCount, total: brief.taskCount }}
        planned={planned}
        behind={planned != null && brief.progress != null && brief.progress < planned && delayed.length > 0}
        nextMs={nextMs}
        lastMs={lastMs}
        end={brief.end}
        forecast={forecast}
        earned={earned}
        today={today}
        extra={reportNotice}
      />

      <div className="sch-ops" role="toolbar" aria-label={t("{schedule}の操作")} data-testid="sch-ops">
        <div className="sch-seg" role="radiogroup" aria-label="表示">
          {(
            [
              ["day", "日"],
              ["jun", "旬"],
              ["month", "月"],
            ] as const
          ).map(([k, t]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={view === k}
              className={view === k ? "on" : ""}
              onClick={() => setView(k)}
              title={k === "jun" ? SCHEDULE_JUN_TITLE : undefined}
              data-testid={`sch-view-${k}`}
            >
              {t}
            </button>
          ))}
        </div>
        {/* 概略（#291）：見出しの行だけ・中の{task}を名前つきで並べる（見るだけ） */}
        <button type="button" className={"btn quiet sch-sumtoggle" + (summary ? " is-on" : "")} aria-pressed={summary} onClick={() => setSummary(!summary)} data-testid="sch-summary">
          {SCHEDULE_MSG.summary}
        </button>
        <div className="sch-nav">
          <button type="button" className="btn quiet sq" aria-label="前へ" onClick={() => scrollBy(-STEP_DAYS[view])}>
            <CrIcon k="back" />
          </button>
          <button type="button" className="btn quiet" onClick={toToday} data-testid="sch-today-btn">
            今日
          </button>
          <button type="button" className="btn quiet sq" aria-label="次へ" onClick={() => scrollBy(STEP_DAYS[view])}>
            <CrIcon k="right" />
          </button>
        </div>
        <span className="sch-period" data-testid="sch-period">
          <span className="long">
            {mdwLongOf(leftDay)}〜 {mdwLongOf(rightDay)}
          </span>
          <span className="short" aria-hidden="true">
            {mdwOf(leftDay)}〜{mdwOf(rightDay)}
          </span>
        </span>
        <span className="hint sch-save" id="sch-autosave" role="status" data-testid="sch-autosave">
          {status.k === "saving" ? SCHEDULE_MSG.saving : status.k === "saved" ? SCHEDULE_MSG.saved : status.k === "error" ? `⚠ ${SCHEDULE_MSG.saveFailed}：${status.msg ?? ""}` : ""}
        </span>
        <span className="sp" />
        {/* 1100px 未満（タブレット）は「見るだけ／直す」のスイッチ（#102 §5.3.4） */}
        {!wide ? <EditSwitch on={editOn} onChange={setEditOn} /> : null}
        {readOnly ? exportButton : null}
        {readOnly ? null : (
          <div className="sch-opsr">
            <button type="button" className="btn quiet sq" aria-label="元に戻す" title="元に戻す（Ctrl＋Z）" disabled={!past.current.length} onClick={undo} data-testid="sch-undo">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
              </svg>
            </button>
            <button type="button" className="btn quiet sq" aria-label="やり直す" title="やり直す（Ctrl＋Shift＋Z）" disabled={!future.current.length} onClick={redo} data-testid="sch-redo">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
              </svg>
            </button>
            <button type="button" className="btn tonal" onClick={() => setAsk({ t: "stops" })} data-testid="sch-stop-btn">
              <CrIcon k={stopIcon()} />
              {SCHEDULE_MSG.stopOpsButton}
            </button>
            <button type="button" className="btn quiet" onClick={() => setAsk({ t: "shiftFrom" })} disabled={!rows.length} data-testid="sch-shift-from-btn">
              <CrIcon k="shift" />
              {SCHEDULE_MSG.shiftFromButton}
            </button>
            {exportButton}
            <RowMenu label={t("{schedule}のメニュー")} items={moreItems} testId="sch-more" />
          </div>
        )}
      </div>

      <DelayBand delayed={delayed} links={doc.links} onKeep={keep} onExtend={extend} readOnly={readOnly} />
      <StartDelayBand delayed={startDelayed} onKeep={keepStart} readOnly={readOnly} />

      <div className={"paper sch-card" + (readOnly ? " ro" : "")} data-testid="sch-card">
        {empty && !readOnly ? (
          <StartCards
            estimates={estimates}
            noProject={!head.groupId}
            templates={tplQ.data?.filter((x) => !x.hidden).length}
            sources={srcQ.data?.length}
            onPick={(k) => {
              if (k === "estimate") setAsk({ t: "fromEstimate" });
              else if (k === "template") setAsk({ t: "fromTemplate" });
              else if (k === "group") setAsk({ t: "fromGroup" });
              else {
                setBlank(true);
                insertAt(0, "task");
              }
            }}
          />
        ) : null}
        <div className="sch-scroll" ref={scroller} onScroll={onScroll} data-testid="sch-scroll">
          <PctContext.Provider value={pct}>
          <Gantt
            doc={doc}
            cal={cal}
            view={view}
            range={range}
            today={today}
            readOnly={readOnly}
            cands={candsQ.data}
            selectedId={selectedId}
            onSelect={setSelectedId}
            ops={ops}
            scrollerRef={scroller}
            left={left}
            leftDay={leftDay}
            showAmount={showAmount}
            delays={delays}
            stops={doc.stops}
            help={<Legend offText={offText} project={L.project} />}
            pxOverride={view === "day" && dayPx ? dayPx : undefined}
            showCrew={showCrew}
            conflicts={conflictOf}
            marks={reportMarks}
            companyOffDays={companyOff}
            summary={summary}
            barDatesAll={barDatesAll}
            cols={cols}
            dayNotes={doc.dayNotes}
          />
          </PctContext.Provider>
        </div>
        {rows.length === 0 && readOnly ? <p className="hint sch-none">{t("{task}はまだありません。")}</p> : null}
        {readOnly ? null : (
          <div className="sch-add" data-testid="sch-add" data-notice-above="">
            <button type="button" className="btn text" onClick={() => insertAt(docRef.current.rows.length, "task")} data-testid="sch-add-task">
              <CrIcon k="plus" />
              {SCHEDULE_MSG.addTask}
            </button>
            <button type="button" className="btn text" onClick={() => insertAt(docRef.current.rows.length, "heading")} data-testid="sch-add-heading">
              <CrIcon k="plus" />
              見出し追加
            </button>
            <button type="button" className="btn text" onClick={() => insertAt(docRef.current.rows.length, "memo")} data-testid="sch-add-memo">
              <CrIcon k="file" />
              メモ追加
            </button>
            <button type="button" className="btn text" onClick={() => insertAt(docRef.current.rows.length, "milestone")} data-testid="sch-add-milestone">
              <span className="sch-dia sm hollow" aria-hidden="true" />
              節目追加
            </button>
          </div>
        )}
      </div>

      {overlays}
      {back.dialog}
      {toastNode}
    </div>
  );
}

/* ---------------- 細い藍の帯（Figma A3。数字のカードはやめて帯の右に数字4つ） ---------------- */
function ScheduleBand({
  head,
  back,
  backLabel,
  rows,
  progress,
  pct,
  done,
  planned,
  behind,
  nextMs,
  lastMs,
  end,
  forecast,
  earned,
  today,
  extra,
}: {
  head: SchedHead;
  back: ReturnType<typeof useBackNav>;
  backLabel: string;
  rows: readonly ScheduleRow[];
  progress: number | null;
  /** 進捗を％で入力（#292。false なら「完了 n／m{task}」＋棒＝完了した{task}の日数 ÷ 全日数） */
  pct: boolean;
  done: { done: number; total: number };
  planned: number | null;
  /** 予定より進みが遅れている（遅れの帯があるとき。% を朱に） */
  behind: boolean;
  nextMs: { name: string; date: string; daysLeft: number } | null;
  lastMs: { name: string; date: string } | null;
  end: string | null;
  forecast: { date: string; days: number } | null;
  earned: { earned: number; total: number } | null;
  today: string;
  /** 見出しの右に足すチップ（協力会社からの報告。#171） */
  extra?: ReactNode;
}) {
  const lastDate = lastMs?.date ?? end;
  // 次の節目は、無いとき・最後の節目と同じ節目のときは出さない。そのときは最後の欄に「・あと n日」（#102 §5.1 の3）
  const showNext = !!nextMs && !(lastMs && nextMs.name === lastMs.name && nextMs.date === lastMs.date);
  const lastLeft = !showNext && lastDate && lastDate >= today ? dayNo(lastDate) - dayNo(today) : null;
  return (
    <header className="sch-band" data-testid="sch-band">
      <div className="sch-bandl">
        <Link className="sch-back" to={back.to} onClick={back.request} title={backTitle(back)} data-testid="create-back">
          <CrIcon k="back" />
          <span className="bkl">
            <span className="bkt">{backLabel}</span>
            <span className="bksr">に戻る</span>
          </span>
        </Link>
        <div className="sch-bandt">
          <h1 title={head.name}>{head.name}</h1>
          <div className="sch-bandc">
            <BandChips head={head} rows={rows} />
            {extra}
          </div>
        </div>
      </div>
      <dl className="sch-nums">
        <div className="sch-num" data-testid="sch-tile-progress">
          <dt>進捗</dt>
          {pct ? (
            <dd>
              <b className={(progress == null ? "zero" : "") + (behind ? " shu" : "")}>{progress == null ? "—" : `${progress}%`}</b>
              {planned != null ? <small data-testid="sch-planned">{SCHEDULE_MSG.planned(planned)}</small> : null}
            </dd>
          ) : (
            <dd className="sch-donebox">
              <b className={done.total ? "" : "zero"} data-testid="sch-done-count">
                {done.total ? SCHEDULE_MSG.doneOf(done.done, done.total) : "—"}
              </b>
              {done.total ? (
                <span className="sch-donebar" role="img" aria-label={`進み具合 ${progress ?? 0}%`} title={t(`完了した{task}の日数 ${progress ?? 0}%`)} data-testid="sch-done-bar">
                  <span style={{ width: `${progress ?? 0}%` }} />
                </span>
              ) : null}
            </dd>
          )}
        </div>
        {showNext && nextMs ? (
          <div className="sch-num" data-testid="sch-tile-next">
            <dt>次の節目</dt>
            <dd>
              <b className="nm" title={nextMs.name}>{nextMs.name || "（名前なし）"}</b>
              <small>
                {mdOf(nextMs.date)}
                {nextMs.daysLeft === 0 ? "・今日" : `・あと${nextMs.daysLeft}日`}
              </small>
            </dd>
          </div>
        ) : null}
        <div className="sch-num" data-testid="sch-tile-last">
          <dt>{lastMs ? lastMs.name || "最後の節目" : "終わり"}</dt>
          <dd>
            <b className={lastDate ? "" : "zero"}>{lastDate ? mdOf(lastDate) : "—"}</b>
            {lastDate ? <span className="sr"> {mdwOf(lastDate)}</span> : null}
            {lastLeft != null ? <small data-testid="sch-last-left">{lastLeft === 0 ? "・今日" : `・あと ${lastLeft}日`}</small> : null}
            {forecast ? (
              <span className="sch-bandpill" title={SCHEDULE_MSG.forecast(mdwOf(forecast.date))} data-testid="sch-forecast">
                {SCHEDULE_MSG.delayBadge(forecast.days)}
                <span className="sr"> {SCHEDULE_MSG.forecast(mdwOf(forecast.date))}</span>
              </span>
            ) : null}
          </dd>
        </div>
        {earned ? (
          <div className="sch-num sch-internal" data-testid="sch-tile-earned" title={SCHEDULE_MSG.earnedOf(`¥${yen(earned.total)}`, earned.total ? Math.floor((earned.earned * 100) / earned.total) : 0)}>
            <dt>{SCHEDULE_MSG.earnedTitle}</dt>
            <dd>
              <b>¥{yen(earned.earned)}</b>
              <span className="sr"> {SCHEDULE_MSG.earnedOf(`¥${yen(earned.total)}`, earned.total ? Math.floor((earned.earned * 100) / earned.total) : 0)}</span>
            </dd>
          </div>
        ) : null}
      </dl>
    </header>
  );
}

/** 凡例と使い方（表の見出しの「？」。#105：凡例の段はやめた） */
function Legend({ offText, project }: { offText: string; project: string }) {
  return (
    <Help
      label="凡例と使い方"
      testId="help-schedule"
      text={
        <>
          <span className="sch-legend" aria-label="凡例">
            <span><span className="sch-sw s-done" />済み</span>
            <span><span className="sch-sw s-plan" />予定</span>
            <span><span className="sch-dia sm hollow" />節目</span>
            <span><span className="sch-sw s-late" />遅れ</span>
            <span><span className="sch-sw s-off" />休日（{offText}）</span>
            <span><span className="sch-sw s-stop">{showsRain() ? "☂" : "止"}</span>中止</span>
            <span><span className="sch-sw s-today" />今日</span>
            <span>
              <svg className="sch-sw s-ln" viewBox="0 0 28 14" aria-hidden="true">
                <path d="M1 3 H10 V11 H26" />
              </svg>
              {t("前の{task}完了後に開始")}
            </span>
          </span>
          {t(
            `バーはドラッグで移動（端で日数を変更、右端の点を次のバーへ引くとつなぎ）。行の「⋮」・開始と日数の欄・バーを選択して ← →（Shift で日数）・Alt＋↑↓（並べ替え）でも同じ操作ができます。前の{task}を後ろへ移動すると、後続の{task}も移動します。休日は日数に含みません（{task}の「⋮」→「休日も作業」で、その{task}のみ休日も含めます）。` +
              // 雨の言い回しは現場力だけ（営業力は「中止」。#142 段5 No.125〜127）
              (showsRain()
                ? "「中止・休み」は、選択した{task}のみ（「雨天も作業」の{task}は除外）か{siteShort}全体を中止、または{siteShort}の休みにします。中止日にかかる{task}は延長されます（見出しの ☂ で解除）。"
                : "「中止・休み」は、選択した{task}のみか{siteShort}全体を中止、または{siteShort}の休みにします。中止日にかかる{task}は延長されます（見出しの「止」で解除）。") +
              `遅れは、昨日までの予定の日数と進捗の比較です。金額は見積から作成した{task}の税抜金額、出来高は 金額 × 進捗 です（社内用・印字しない）。{task}・担当の列の幅は内容に合わせます。表とバーの境のドラッグ（選択して ← → でも）で変更、境のダブルクリックか「⋮」→「表示設定」の表の幅「自動」で元に戻ります。${project}の画面の「{schedule}」から開きます。`,
          )}
        </>
      }
    />
  );
}

/* ---------------- 確かめ・選ぶ ---------------- */
function HeadingDelete({ name, n, onCancel, onDelete }: { name: string; n: number; onCancel: () => void; onDelete: (mode: "row" | "block") => void }) {
  const [mode, setMode] = useState<"row" | "block">("row");
  useEsc(onCancel);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="dialog" aria-label={SCHEDULE_MSG.headingDeleteTitle(name)} data-testid="sch-heading-delete">
        <div className="dh">{SCHEDULE_MSG.headingDeleteTitle(name)}</div>
        <div className="db" role="radiogroup">
          <label className="ck">
            <input type="radio" name="hdel" checked={mode === "row"} onChange={() => setMode("row")} autoFocus />
            {SCHEDULE_MSG.headingOnly}
          </label>
          <label className="ck">
            <input type="radio" name="hdel" checked={mode === "block"} onChange={() => setMode("block")} />
            {SCHEDULE_MSG.headingAll(n)}
          </label>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn shu" onClick={() => onDelete(mode)} data-testid="sch-heading-delete-ok">
            削除
          </button>
        </div>
      </div>
    </div>
  );
}

function PredPicker({ doc, id, onCancel, onOk }: { doc: ScheduleDoc; id: string; onCancel: () => void; onOk: (links: ScheduleDoc["links"]) => void }) {
  const me = doc.rows.find((r) => r.id === id)!;
  const others = doc.links.filter((l) => l.to !== id);
  const [on, setOn] = useState(() => new Set(doc.links.filter((l) => l.to === id).map((l) => l.from)));
  useEsc(onCancel);
  const cands = doc.rows.filter((r) => r.id !== id && (r.kind === "task" || r.kind === "milestone"));
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="dialog" aria-label={t(`「${me.name || "{task}"}」の前の{task}`)} data-testid="sch-preds">
        <div className="dh">{t(`「${me.name || "{task}"}」の前の{task}`)}</div>
        <div className="db sch-predlist">
          {cands.length === 0 ? <p style={{ margin: 0 }}>{t("ほかに{task}がありません。")}</p> : null}
          {cands.map((r) => {
            const cyc = wouldCreateCycle(others, r.id, id);
            return (
              <label key={r.id} className={"chk ck" + (cyc ? " dim2" : "")}>
                <input
                  type="checkbox"
                  checked={on.has(r.id)}
                  disabled={cyc}
                  onChange={(e) => {
                    const next = new Set(on);
                    if (e.target.checked) next.add(r.id);
                    else next.delete(r.id);
                    setOn(next);
                  }}
                />
                <span>{r.name || "（名前なし）"}</span>
                <small className="muted">{r.start ? mdwOf(r.start) : "日付未定"}{cyc ? "・循環するため選択できません" : ""}</small>
              </label>
            );
          })}
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" onClick={() => onOk([...others, ...[...on].map((from) => ({ from, to: id }))])} data-testid="sch-preds-ok">
            決定
          </button>
        </div>
      </div>
    </div>
  );
}

function ShiftDialog({ name, onCancel, onOk }: { name: string; onCancel: () => void; onOk: (n: number) => void }) {
  const [text, setText] = useState("1");
  useEsc(onCancel);
  const n = Number(text.normalize("NFKC").replace(/[−–—ー―‐]/g, "-").replace(/[^\d-]/g, "")) || 0;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="dialog" aria-label="後続と一括移動" data-testid="sch-shift">
        <div className="dh">後続と一括移動</div>
        <div className="db">
          <p style={{ margin: 0 }}>{t(`「${name || "{task}"}」と後続の{task}を稼働日で移動します。`)}</p>
          <label className="sch-shiftin">
            <input className="inp num" type="text" inputMode="numeric" value={text} onChange={(e) => setText(e.target.value)} aria-label="移動日数（前はマイナス）" autoFocus />
            日（前は −）
          </label>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={!n || Math.abs(n) > 999} onClick={() => onOk(n)} data-testid="sch-shift-ok">
            移動
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleDelete({ onCancel, onDelete }: { onCancel: () => void; onDelete: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  useEsc(onCancel);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="alertdialog" aria-label={SCHEDULE_MSG.scheduleDeleteTitle} data-testid="sch-delete-confirm">
        <div className="dh">{SCHEDULE_MSG.scheduleDeleteTitle}</div>
        <div className="db">
          <p style={{ margin: 0 }}>{SCHEDULE_MSG.scheduleDeleteBody}</p>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel} autoFocus>
            取消
          </button>
          <button
            type="button"
            className="btn shu"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onDelete();
            }}
            data-testid="sch-delete-ok"
          >
            削除
          </button>
        </div>
      </div>
    </div>
  );
}
